import { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useState } from "react";

import { EmailContentFlow } from "@simplyk/common";
import { useSnackbar } from "notistack";

import { useEmailEditorCapabilities } from "../EmailEditorDevice/useEmailEditorCapabilities";
import { OpenEmailEditorDefaultValues, useOpenEmailEditor } from "../EmailEditor/useOpenEmailEditor";

import { EmailChoiceSource, EMAIL_CHOICE_SOURCE_BY_ENTRY_POINT, EmailTypeChoiceEntryPoint } from "./emailChoiceSource";
import { EmailTypeChoice, EmailTypeChoiceDrawer } from "./EmailTypeChoiceDrawer";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useMagicEmailFlowFlag } from "@/common/hooks/useMagicEmailFlowFlag";
import { useUrlBuilder } from "@/common/hooks/useUrlBuilder";

export type EmailTypeChoiceRequest = Omit<OpenEmailEditorDefaultValues, "flow" | "entryPoint"> & {
  entryPoint: EmailTypeChoiceEntryPoint;
  queryParamsToExclude?: string[];
  onExitUrl?: string;
  designedDestination?: "builder" | "gallery";
};

interface EmailTypeChoiceContextProps {
  openEmailTypeChoice: (request: EmailTypeChoiceRequest) => void;
  isEmailTypeChoiceOpen: boolean;
}

const EmailTypeChoiceContext = createContext<EmailTypeChoiceContextProps>({
  openEmailTypeChoice: () => {},
  isEmailTypeChoiceOpen: false,
});

const FLOW_BY_CHOICE: Record<EmailTypeChoice, EmailContentFlow> = {
  designed: EmailContentFlow.Builder,
  simple: EmailContentFlow.Quick,
};

const resolveListAudienceForSimple = (
  editorRequest: Omit<EmailTypeChoiceRequest, "designedDestination">
): Omit<EmailTypeChoiceRequest, "designedDestination"> => {
  const { contactListIds, ...rest } = editorRequest;
  if (!contactListIds?.length) {
    return editorRequest;
  }
  return {
    ...rest,
    contactSelection: {
      mode: "filter",
      filterParams: {
        filters: [{ type: "list", operation: "is", values: contactListIds, id: "email-audience-lists" }],
        combinator: "AND",
      },
    },
  };
};

export const EmailTypeChoiceProvider: FC<PropsWithChildren> = ({ children }) => {
  const { logAmplitudeEvent } = useAmplitude();
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { openEmailEditor, createEmailContentWithoutOpening, isLoading } = useOpenEmailEditor();
  const { isActive: isMagicEmailFlowActive } = useMagicEmailFlowFlag();
  const capabilities = useEmailEditorCapabilities();
  const buildUrl = useUrlBuilder();
  const [request, setRequest] = useState<EmailTypeChoiceRequest>();

  const openEmailTypeChoice = useCallback(
    (nextRequest: EmailTypeChoiceRequest) => {
      setRequest(nextRequest);
      logAmplitudeEvent(AmplitudeEvents.EmailChoiceDrawerViewed, {
        from: EMAIL_CHOICE_SOURCE_BY_ENTRY_POINT[nextRequest.entryPoint] satisfies EmailChoiceSource,
      });
    },
    [logAmplitudeEvent]
  );

  const handleChoose = useCallback(
    async (choice: EmailTypeChoice) => {
      if (!request) {
        return;
      }
      const { designedDestination, ...editorRequest } = request;
      logAmplitudeEvent(AmplitudeEvents.EmailChoiceDrawerTypeSelected, {
        choice,
        from: EMAIL_CHOICE_SOURCE_BY_ENTRY_POINT[request.entryPoint] satisfies EmailChoiceSource,
      });

      try {
        if (
          choice === "designed" &&
          designedDestination === "gallery" &&
          isMagicEmailFlowActive &&
          capabilities.creationMethodPicker
        ) {
          const created = await createEmailContentWithoutOpening({
            ...editorRequest,
            flow: EmailContentFlow.Builder,
          });
          if (!created) {
            enqueueSnackbar(t("dashboard", "EmailEditor.error.failToCreateEmail"), { vibe: "danger" });
            return;
          }
          setRequest(undefined);
          await openEmailEditor({
            emailContentId: created.id,
            queryParamsToExclude: request.queryParamsToExclude,
            onExitUrl: request.onExitUrl ?? buildUrl({}),
            isCreationFlow: true,
          });
          return;
        }

        const choiceRequest = choice === "simple" ? resolveListAudienceForSimple(editorRequest) : editorRequest;
        const opened = await openEmailEditor({ ...choiceRequest, flow: FLOW_BY_CHOICE[choice] });
        if (opened) {
          setRequest(undefined);
        }
      } catch {
        enqueueSnackbar(t("dashboard", "EmailEditor.error.failToCreateEmail"), { vibe: "danger" });
        setRequest(undefined);
      }
    },
    [
      buildUrl,
      capabilities,
      createEmailContentWithoutOpening,
      enqueueSnackbar,
      isMagicEmailFlowActive,
      logAmplitudeEvent,
      openEmailEditor,
      request,
      t,
    ]
  );

  const isEmailTypeChoiceOpen = Boolean(request);
  const contextValue = useMemo(
    () => ({ openEmailTypeChoice, isEmailTypeChoiceOpen }),
    [openEmailTypeChoice, isEmailTypeChoiceOpen]
  );

  return (
    <EmailTypeChoiceContext.Provider value={contextValue}>
      {children}
      <EmailTypeChoiceDrawer
        open={isEmailTypeChoiceOpen}
        disabled={isLoading}
        onClose={() => !isLoading && setRequest(undefined)}
        onChoose={(choice) => void handleChoose(choice)}
      />
    </EmailTypeChoiceContext.Provider>
  );
};

export const useEmailTypeChoice = () => useContext(EmailTypeChoiceContext);
